import { createSlice, CaseReducer, PayloadAction } from '@reduxjs/toolkit';

import { get, del } from 'api/requests';

import { selectCalendarParams } from 'pages/Timesheet/TimesheetEngNetworks/selectors';

import { TOTAL } from 'features/Timesheet/TimesheetEngNetworks/const';

import { getMinutesFromTime } from 'helpers';

import { getDistribution } from './distributionSlice';

import { AppThunk } from 'store/types';
import { IDraggableCard, IDataCard, IParamsReq } from 'types';
import {
  ITimeItem,
  ITimeItemRaw,
  ICalendarParamsReq,
} from 'pages/Timesheet/TimesheetEngNetworks/types';

interface ICalendarStore {
  list: ITimeItem[];
  loading: boolean;
}

const initialState: ICalendarStore = {
  list: [],
  loading: false,
};

const reset: CaseReducer<ICalendarStore> = () => initialState;

const loading: CaseReducer<ICalendarStore, PayloadAction<boolean>> = (state, action) => {
  state.loading = action.payload;
};

const list: CaseReducer<ICalendarStore, PayloadAction<ITimeItem[]>> = (state, action) => {
  state.list = action.payload;
};

export const calendarSlice = createSlice({
  name: 'calendar', // 'INSPECTION_REQUESTS/calendar',
  initialState,
  reducers: {
    reset,
    loading,
    list,
  },
});

const { reset: setReset, loading: setLoading, list: setCalendar } = calendarSlice.actions;

export const removeCalendar =
  (task: IDraggableCard, comment: string, search: IParamsReq): AppThunk =>
  (dispatch, getState) => {
    const state = getState();
    const params = selectCalendarParams(state);

    const { project, structure, network, subnet } = search;

    dispatch(setLoading(true));

    del(`/api/v2/timesheet/meeting-engineer/${task.id}/`, { comment: comment })
      .then(() => {
        dispatch(
          getCalendar({
            start_date: params.start_date.split('.').reverse().join('-'),
          }),
        );
        dispatch(getDistribution({ project, structure, network, subnet }));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const fetchCalendar =
  (params: ICalendarParamsReq): AppThunk =>
  (dispatch, getState) => {
    const { timesheet } = getState();
    const { engineeringCalendar } = timesheet;
    const {
      calendar: { list },
    } = engineeringCalendar;

    const isFirstLoading = !list.length;

    isFirstLoading && dispatch(setLoading(true));

    get<ITimeItemRaw[]>('/api/timesheet/engineering/', params)
      .then(({ data }) => {
        return dispatch(
          setCalendar(
            data.map(item => ({
              ...item,
              meetings: item.meetings.map((meeting: IDataCard) => ({
                ...meeting,
                duration:
                  getMinutesFromTime(meeting.endTime) - getMinutesFromTime(meeting.startTime),
                parentId: item.date,
                structureName: '',
              })),
            })),
          ),
        );
      })
      .finally(() => {
        isFirstLoading && dispatch(setLoading(false));
      });
  };

export const getCalendar =
  (params: ICalendarParamsReq, count = TOTAL): AppThunk =>
  dispatch => {
    dispatch(fetchCalendar({ ...params, count }));
  };

export { setReset };
